import {
  useCallback,
  useMemo,
  useRef,
  useState,
  KeyboardEvent,
  PointerEvent,
  useEffect,
} from 'react';
import { Chess, Move } from 'chess.js';

import { AudioManager } from '@libs/audio';
import { PieceCode, Color, SquareName, PromotionPiece, ChessWinner } from '@libs/chess/types';
import { delay } from '@libs/delay';
import { usePrev } from '@libs/hooks/usePrev';

import { Props, UserArrow } from '../ChessBoard';
import { BOARD_SOUND_THEMES, BoardSoundThemes } from '../sounds';
import { toggleUserArrow } from '../utils/toggleUserArrow';
import {
  Action,
  AllowedAction,
  initSelection,
  SelectionFSM,
  SelectionIdle,
  SelectionSelected,
} from './useChessGame.fsm';

/*
    ____,-------------------------------,____
    \   |              Hook             |   /
    /___|-------------------------------|___\
*/

export type Args = {
  side: 'white' | 'black' | 'all' | 'none';
  // uncontrolled:
  initialFen?: string;
  initialSelection?: SquareSelection;
  initialArrows?: UserArrow[];
  // controlled:
  gameId?: string;
  fen?: string;
  selection?: SquareSelection;
  //
  allowPremove?: boolean;
  autoPromotion?: PromotionPiece;
  //
  boardSounds?: BoardSoundThemes;
  winner?: ChessWinner | null;
  player?: 'white' | 'black';
  //
  onMove?: (m: MoveInfo, san: string, fen: string) => void;
  onPremove?: (m: MoveInfo | null) => void;
};

export type SquareSelection = {
  from: SquareName;
  to: SquareName;
  action: Action;
};

export type Value = {
  reset: (fen?: string) => void;
  move: (move: ExternalMove) => void;

  props: MappedProps;
};

export type MoveInfo = {
  from: SquareName;
  to: SquareName;
  promotion?: PromotionPiece;
};

export type ExternalMove = {
  fen: string;
  prevFen: string;
  from: SquareName;
  to: SquareName;
  promotion?: PromotionPiece;
};

type MappedProps = Pick<
  Props,
  | 'position'
  | 'interactive'
  //
  | 'isPremove'
  | 'selectedFrom'
  | 'selectedTo'
  | 'premoveFrom'
  | 'premoveTo'
  | 'moves'
  | 'promotingFrom'
  | 'promotingTo'
  | 'arrows'
  //
  | 'winner'
  //
  | 'onPieceDrag'
  | 'onSquareClick'
  | 'onSquarePointerDown'
  | 'onSquareDrop'
  | 'onDropOutside'
  | 'onDragAbort'
  | 'onPromotion'
  | 'onKeyDown'
  | 'onArrowDrag'
  | 'onArrowPlace'
>;

export function useChessGame({
  side,
  initialFen,
  initialSelection,
  initialArrows,
  gameId,
  fen,
  selection,
  allowPremove = false,
  autoPromotion,
  boardSounds,
  winner,
  player,
  onMove,
  onPremove,
}: Args): Value {
  if (side === 'all' && allowPremove) {
    throw new Error('useChessGame: allowPremove is not supported together with side=all');
  }

  const prevGameId = usePrev(gameId);
  const prevFen = usePrev(fen);
  const prevSelection = usePrev(selection);
  const fenChanged = prevFen !== fen || gameId !== prevGameId;
  const selectionChanged = prevSelection !== selection;

  // ------------------------------------------
  // Store and state

  // sic! useState используется только для инициализации и форсирования
  // перерисовки компонента из обработчиков событий, логика же полагается на
  // mutable ref. Избегание useState помогает реализовать аналог controlled
  // components и избежать лишних перерисовок и вещей вроде setState внутри useEffect
  const [initialSnapshot, _redraw] = useState(() => ({
    chess: new Chess(fen ?? initialFen, { enable960: true }),
    state: initSelection(selection ?? initialSelection),
    arrows: initialArrows,
  }));
  const refSnapshot = useRef(initialSnapshot);

  // ---

  if (fenChanged) {
    if (fen) {
      refSnapshot.current.chess.load(fen);
    } else {
      refSnapshot.current.chess.reset();
    }
  }

  if (fenChanged || selectionChanged) {
    refSnapshot.current.state = initSelection(selection);
    refSnapshot.current.arrows = undefined;
  }

  // ---

  const { chess, state, arrows } = refSnapshot.current;

  const gameOver = chess.isGameOver();
  const turnColor: Color = chess.turn();
  const allow = mapAllowedAction(side, turnColor, allowPremove, gameOver);

  const effectiveWinner = gameOver ? mapWinner(chess) : (winner ?? undefined);

  // ---

  //
  // Обернём все необходимые данные в ref для доступа в коллбэках
  //
  const store = {
    side,
    allowPremove,
    autoPromotion,
    state,
    chess,
    allow,
    effectiveWinner,
    onMove,
    onPremove,
    updateState: (s: SelectionFSM) => {
      if (refSnapshot.current.state !== s) {
        refSnapshot.current.state = s;
        _redraw({ ...refSnapshot.current });
      }
    },
    updateArrows: (a?: UserArrow[]) => {
      if (refSnapshot.current.arrows !== a) {
        refSnapshot.current.arrows = a;
        _redraw({ ...refSnapshot.current });
      }
    },
  };
  const refStore = useRef(store);
  refStore.current = store;

  // ---

  //
  // Чтобы не городить с независимыми состояниями для selection и premove, используем
  // prevMoveRef для хранения последнего хода
  //

  const isPremove = state.action === 'premove';
  const prevMoveRef = useRef(state);

  if ((state.name === 'selected' && state.action === 'move') || fenChanged) {
    prevMoveRef.current = state;
  }

  // Использовать prevMoveRef при премуве или пока нет своего хода или если матч
  // закончился пока игрок выбирал ход
  // (чтобы был виден ход последний противника)
  const showPrev =
    !fenChanged &&
    (allow === 'premove' ||
      state.name === 'idle' ||
      (state.name === 'targeting' && effectiveWinner));

  // ---

  const soundRef = useSoundsPlayer(boardSounds, player, winner);

  // ------------------------------------------
  // Moves and Premoves

  const handleMove = useCallback(
    (move: Move, mode: 'drop' | 'full' = 'full') => {
      const { chess, onMove, onPremove, state, autoPromotion } = refStore.current;
      const m =
        autoPromotion && move.promotion
          ? {
              from: move.from,
              to: move.to,
              promotion: move.promotion as PromotionPiece,
            }
          : {
              from: move.from,
              to: move.to,
              piece: mapMoveToPieceCode(move),
            };

      if (state.action === 'premove') {
        onPremove?.(m);

        soundRef.current.move('drop'); // sic! всегда drop т.к. нет анимаций
      } else {
        const cm = chess.move(move);
        const san = cm.san;
        onMove?.(m, san, chess.fen());

        if (cm.captured) {
          soundRef.current.capture(mode);
        } else {
          soundRef.current.move(mode);
        }
      }
    },
    [soundRef],
  );

  // ------------------------------------------
  // Selection

  //
  // Выбор клетки происходит по ponterdown, а вот снятие двухфазное:
  //  - PointerDown: устанавливает флаг (в SelectionTargeting.transition)
  //  - Click: проверяет флаг и отменяет выбор клетки (в SelectionTargeting.transitionCleanup)
  //
  // Без этого при перетаскивании ранее выбранной фигуры выбор сначала снимется,
  // и вернётся при перемещении мышки
  //

  const handlePointerDown = useCallback(
    (s: SquareName, _: unknown, e: PointerEvent) => {
      const { chess, state, updateState, updateArrows, allow, onPremove } = refStore.current;

      // сбросить стрелочки при ЛКМ
      if (e.button === 0 && !e.ctrlKey) updateArrows(undefined);

      // игнорировать ЛКМ при зажатом ctrl (это режим рисования стрелочек)
      if (e.ctrlKey) return;

      if (allow === 'none' || e.button !== 0) return;

      let next: SelectionFSM =
        state.name === 'promoting' || state.name === 'selected' ? state.toIdle() : state;

      // Сбросим premove
      if (state.name === 'selected' && state.action === 'premove') {
        onPremove?.(null);
      }

      // CORNER CASE: клик на туже пешку, которую двигали, но уже в новой клетке
      if (state.name === 'promoting' && state.to === s) {
        updateState(next);
        return;
      }

      switch (next.name) {
        case 'idle':
          next = next.transition({
            from: s,
            allowedAction: allow,
            chess,
          });
          break;
        case 'targeting':
          next = next.transition({
            to: s,
            chess,
            canReset: true,
            canReselect: true,
            moveCallback: (m) => handleMove(m),
            autoPromotion,
          });
          break;
      }

      if (next.name === 'promoting') {
        soundRef.current.move('full');
      }

      updateState(next);
    },
    [autoPromotion, handleMove, soundRef],
  );

  const handleClick = useCallback(() => {
    const { state, updateState: update } = refStore.current;

    if (state.name === 'targeting') {
      update(state.transitionCleanup());
    }
  }, []);

  // ------------------------------------------
  // Drag

  const handleDrag = useCallback((s: SquareName) => {
    const { allow, state, updateState, chess } = refStore.current;

    if (state.name === 'idle') {
      updateState(
        state.transition({
          allowedAction: allow,
          from: s,
          chess,
        }),
      );

      return true;
    }

    return state.name === 'targeting' && state.from === s;
  }, []);

  const handleDrop = useCallback(
    (_from: SquareName, to: SquareName) => {
      const { state, updateState, chess, effectiveWinner } = refStore.current;

      if (state.name !== 'targeting' || effectiveWinner) return;

      const next = state.transition({
        to,
        chess,
        canReset: true,
        canReselect: false,
        moveCallback: (m) => handleMove(m, 'drop'),
        autoPromotion,
      });

      updateState(next);

      if (next.name === 'promoting') {
        soundRef.current.move('drop');
      }
    },
    [autoPromotion, handleMove, soundRef],
  );

  const handleDropOutside = useCallback(() => {
    const { updateState } = refStore.current;
    updateState(new SelectionIdle());
  }, []);

  // ------------------------------------------
  // Promotion

  const handlePromotion = useCallback((promoteTo: PromotionPiece) => {
    const { state, updateState, chess, onMove, onPremove } = refStore.current;

    if (state.name !== 'promoting') return;

    const move: MoveInfo = {
      from: state.promotion.from,
      to: state.promotion.to,
      promotion: promoteTo,
    };

    // --- PREMOVE

    if (state.action === 'premove') {
      updateState(state.toSelected());
      onPremove?.(move);

      return;
    }

    // --- MOVE

    const san = chess.move({ ...state.promotion, promotion: promoteTo }).san;
    updateState(state.toSelected());
    onMove?.(move, san, chess.fen());
  }, []);

  // ------------------------------------------
  // Keyboard

  const handleKeyboard = useCallback((e: KeyboardEvent) => {
    const { state, updateState, onPremove } = refStore.current;

    if (
      e.code === 'Escape' &&
      state.name !== 'idle' &&
      (state.name !== 'selected' || state.action === 'premove')
    ) {
      updateState(state.toIdle());

      if (state.action === 'premove') {
        onPremove?.(null);
      }
    }
  }, []);

  // ------------------------------------------
  // Arrows

  const handleArrow = useCallback((arrow: UserArrow) => {
    const { updateArrows } = refStore.current;
    updateArrows(toggleUserArrow(refSnapshot.current.arrows, arrow));
  }, []);

  const handleArrowDrag = useCallback(() => {
    const { state, updateState: update } = refStore.current;

    if (state.name !== 'idle' && state.name !== 'selected') {
      update(state.toIdle());
    }

    return true;
  }, []);

  // ------------------------------------------
  // External commands

  const handleExternalReset = useCallback((fen?: string) => {
    const { updateState, updateArrows, chess } = refStore.current;

    // сбросить стрелочки
    updateArrows(undefined);

    if (fen) {
      chess.load(fen);
    } else {
      chess.reset();
    }

    const nextState = new SelectionIdle();
    prevMoveRef.current = nextState;
    updateState(nextState);
  }, []);

  const handleExternalMove = useCallback(
    ({ from, to, promotion, fen, prevFen }: ExternalMove) => {
      const { chess, state, updateState, updateArrows, allowPremove, side } = refStore.current;

      // сбросить стрелочки
      updateArrows(undefined);

      let nextState: SelectionFSM = new SelectionSelected('move', from, to);
      // sic! сразу сохраним state с ходом соперника, даже если ниже подменим его на свой premove
      prevMoveRef.current = nextState;

      const internalFen = chess.fen().split(' ')[0];
      if (prevFen.split(' ')[0] === internalFen) {
        const m = chess.move({ from, to, promotion });

        if (m.captured) {
          soundRef.current.capture('full');
        } else {
          soundRef.current.move('full');
        }

        //
        // Обработать ситуацию, что во время premove противник сделал ход
        //

        if (state.name === 'targeting' && state.action === 'premove') {
          nextState = new SelectionIdle().transition({
            allowedAction: mapAllowedAction(side, chess.turn(), allowPremove, chess.isGameOver()),
            from: state.from,
            chess: chess,
          });
        }
      } else if (fen.split(' ')[0] !== internalFen) {
        chess.load(fen);
      }

      updateState(nextState);
    },
    [soundRef],
  );

  // ------------------------------------------
  // Return

  const moves = useMemo(() => state.moves?.map((m) => m.to), [state.moves]);

  return {
    move: handleExternalMove,
    reset: handleExternalReset,
    props: {
      position: chess.fen(),
      interactive: mapInteractive(allow, turnColor),
      isPremove,
      selectedFrom: !showPrev ? state.from : prevMoveRef.current.from,
      selectedTo: !showPrev ? state.to : prevMoveRef.current.to,
      premoveFrom: isPremove ? state.from : undefined,
      premoveTo: isPremove ? state.to : undefined,
      moves: effectiveWinner ? undefined : moves,
      promotingFrom: state.promotion?.from,
      promotingTo: state.promotion?.to,
      winner: effectiveWinner,
      arrows,
      onPieceDrag: handleDrag,
      onSquareClick: handleClick,
      onSquarePointerDown: handlePointerDown,
      onSquareDrop: handleDrop,
      onDropOutside: handleDropOutside,
      onDragAbort: handleDropOutside,
      onPromotion: handlePromotion,
      onKeyDown: handleKeyboard,
      onArrowDrag: handleArrowDrag,
      onArrowPlace: handleArrow,
    },
  };
}

/*
    ____,-------------------------------,____
    \   |             Hooks             |   /
    /___|-------------------------------|___\
*/

function useSoundsPlayer(
  sounds: Args['boardSounds'],
  player: Args['player'],
  winner?: ChessWinner | null,
) {
  const MOVE_DELAY_MS = 310;
  const CAPTURE_DELAY_MS = 210;
  const RESULT_DELAY_MS = 350;
  const DUMMY = useMemo(() => {
    const p = Promise.resolve() as Promise<unknown>;
    return {
      move: (_mode: 'drop' | 'full' = 'full') => p,
      capture: (_mode: 'drop' | 'full' = 'full') => p,
    };
  }, []);

  const pack = useRef(DUMMY);
  const prevWiner = usePrev(winner);

  useEffect(() => {
    const resources = sounds && BOARD_SOUND_THEMES[sounds];

    if (resources) {
      AudioManager.instance.load(resources).then((p) => {
        pack.current = {
          move: (mode: 'drop' | 'full' = 'full') => {
            switch (mode) {
              case 'drop':
                return p.move.play();
              case 'full':
                return delay(MOVE_DELAY_MS).then(() => p.move.play());
            }
          },
          capture: (mode: 'drop' | 'full' = 'full') => {
            switch (mode) {
              case 'drop':
                return p.capture.play();
              case 'full':
                return delay(CAPTURE_DELAY_MS).then(() => p.capture.play());
            }
          },
        };
      });
    } else {
      pack.current = DUMMY;
    }
  }, [DUMMY, sounds]);

  // Озвучивание результата игры

  if (prevWiner === null && winner && winner !== null) {
    delay(RESULT_DELAY_MS).then(() => {
      // SIC: пока выключим звуки победы и поражения — они слишком раздажающие
      if (winner === player || winner === 'draw') {
        // pack.current.win();
      } else {
        // pack.current.loose();
      }
    });
  }

  return pack;
}

/*
    ____,-------------------------------,____
    \   |            Mappers            |   /
    /___|-------------------------------|___\
*/

function mapAllowedAction(
  side: Args['side'],
  turnColor: Color,
  allowPremove: boolean,
  gameOver: boolean,
): AllowedAction {
  if (gameOver || side === 'none') return 'none';
  if (side === 'all') return 'move';

  const turnSide = turnColor === 'w' ? 'white' : 'black';

  if (side === turnSide) return 'move';
  return allowPremove ? 'premove' : 'none';
}

function mapInteractive(allow: AllowedAction, turnColor: Color): Required<Props>['interactive'] {
  switch (allow) {
    case 'none':
      return 'none';
    case 'move':
      return turnColor === 'w' ? 'white' : 'black';
    case 'premove':
      return turnColor === 'w' ? 'black' : 'white';
  }
}

function mapMoveToPieceCode(move: Move): PieceCode {
  return `${move.color}${move.piece.toLocaleUpperCase()}` as PieceCode;
}

function mapWinner(chess: Chess): 'draw' | 'white' | 'black' {
  if (chess.isDraw()) return 'draw';
  return chess.turn() === 'b' ? 'white' : 'black';
}
